/**
 * Client device-key proof of possession (AR-2b Option C, ADR-0039).
 *
 * The app holds a per-device P-256 key. At login and on every refresh the client proves possession by signing an
 * **RFC 9449 DPoP proof JWT** — `{ typ: "dpop+jwt", alg: "ES256", jwk }` over `{ jti, htm, htu, iat }` (+ the Vello
 * `rth` claim = base64url(SHA-256(refresh token)) on a refresh). The server binds the session to the key's RFC 7638
 * thumbprint at login and refuses a refresh that is not signed by that key (per the realm's rollout mode).
 *
 * The key lives behind an injected {@link DeviceProofSigner} port, so each platform supplies its own:
 *  - web: `createWebCryptoDeviceSigner` (WebCrypto ECDSA P-256, non-extractable key persisted in IndexedDB);
 *  - native (Rider/Driver): NOT built yet — Hermes has no SubtleCrypto; see ADR-0039 §8 for the follow-up.
 * This module only assembles and encodes the JWS; it never touches key material.
 */
import { uuidv7 } from '@vello/core';
const B64URL_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
/** Unpadded base64url (RFC 4648 §5) — no `Buffer`/`btoa`, so it runs in browsers, Node, and Hermes alike. */
export function base64UrlEncode(bytes) {
    let out = '';
    let i = 0;
    for (; i + 2 < bytes.length; i += 3) {
        const n = ((bytes[i] ?? 0) << 16) | ((bytes[i + 1] ?? 0) << 8) | (bytes[i + 2] ?? 0);
        out += B64URL_ALPHABET[(n >> 18) & 63] + B64URL_ALPHABET[(n >> 12) & 63] + B64URL_ALPHABET[(n >> 6) & 63] + B64URL_ALPHABET[n & 63];
    }
    const rest = bytes.length - i;
    if (rest === 1) {
        const n = (bytes[i] ?? 0) << 16;
        out += B64URL_ALPHABET[(n >> 18) & 63] + B64URL_ALPHABET[(n >> 12) & 63];
    }
    else if (rest === 2) {
        const n = ((bytes[i] ?? 0) << 16) | ((bytes[i + 1] ?? 0) << 8);
        out += B64URL_ALPHABET[(n >> 18) & 63] + B64URL_ALPHABET[(n >> 12) & 63] + B64URL_ALPHABET[(n >> 6) & 63];
    }
    return out;
}
const utf8 = (s) => new TextEncoder().encode(s);
const runtimeCrypto = () => globalThis.crypto;
const defaultSha256 = async (bytes) => {
    const subtle = runtimeCrypto()?.subtle;
    if (subtle === undefined)
        throw new Error('SHA-256 is unavailable in this runtime');
    return new Uint8Array(await subtle.digest('SHA-256', bytes));
};
/** A unique proof id: a CSPRNG UUID when the runtime has one, else a UUIDv7 (uniqueness is all `jti` needs). */
const defaultJti = () => runtimeCrypto()?.randomUUID?.() ?? uuidv7();
/** Strip query and fragment from a URL without depending on a `URL` implementation. */
export function proofHtu(url) {
    const cut = url.search(/[?#]/);
    return cut === -1 ? url : url.slice(0, cut);
}
/** Assemble and sign one compact device proof. */
export async function mintDeviceProof(signer, input) {
    const jwk = await signer.publicJwk();
    const header = { typ: 'dpop+jwt', alg: 'ES256', jwk: { kty: jwk.kty, crv: jwk.crv, x: jwk.x, y: jwk.y } };
    const claims = { jti: input.jti, htm: input.htm, htu: proofHtu(input.htu), iat: input.iat };
    if (input.refreshToken !== undefined) {
        claims.rth = base64UrlEncode(await (input.sha256 ?? defaultSha256)(utf8(input.refreshToken)));
    }
    const signingInput = `${base64UrlEncode(utf8(JSON.stringify(header)))}.${base64UrlEncode(utf8(JSON.stringify(claims)))}`;
    const signature = await signer.sign(utf8(signingInput));
    if (signature.length !== 64)
        throw new Error('an ES256 JWS signature must be 64 bytes (r||s)');
    return `${signingInput}.${base64UrlEncode(signature)}`;
}
/** Adapt a signer to the `@vello/api-client` proof hook (inject it as `ApiClientConfig.deviceProof`). */
export function deviceProofProvider(signer, options = {}) {
    return (request) => mintDeviceProof(signer, {
        htm: request.method,
        htu: request.url,
        iat: Math.floor((options.nowMs ?? Date.now)() / 1000),
        jti: (options.jti ?? defaultJti)(),
        ...(request.refreshToken !== undefined ? { refreshToken: request.refreshToken } : {}),
        ...(options.sha256 !== undefined ? { sha256: options.sha256 } : {}),
    });
}
//# sourceMappingURL=device-proof.js.map